import { useCallback, useEffect, useSyncExternalStore } from 'react';

import { useUpdatingRef } from '@hooks/useUpdatingRef';

import { dictationController } from '../DictationController';
import { DictationErrorKind } from '../types';

export interface UseDictationResult {
    /** This field is the current dictation target (connecting or listening). */
    isDictating: boolean;
    isStarting: boolean;
    error: DictationErrorKind | null;
    hasFailedUtterance: boolean;
    toggleDictation(): void;
}

export function useDictation(
    fieldId: string,
    getText: () => string,
    setText: (text: string) => void,
    enabled: boolean,
): UseDictationResult {
    const state = useSyncExternalStore(dictationController.subscribe, dictationController.getState);

    const getTextRef = useUpdatingRef(getText);
    const setTextRef = useUpdatingRef(setText);

    const toggleDictation = useCallback(() => {
        void dictationController.toggle({
            fieldId,
            getText: () => getTextRef.current(),
            setText: text => setTextRef.current(text),
        });
    }, [fieldId, getTextRef, setTextRef]);

    useEffect(() => {
        if (!enabled) {
            void dictationController.stopIfActive(fieldId);
        }
    }, [enabled, fieldId]);

    useEffect(() => {
        return () => {
            void dictationController.stopIfActive(fieldId);
        };
    }, [fieldId]);

    const isDictating = state.activeFieldId === fieldId;

    return {
        isDictating,
        isStarting: isDictating && state.phase === 'starting',
        error: state.error?.fieldId === fieldId ? state.error.kind : null,
        hasFailedUtterance: state.failedUtteranceFieldId === fieldId,
        toggleDictation,
    };
}
